import { useCallback, useState } from 'react';
import { Outlet } from 'react-router-dom';

import { Job, OutletContext, PlatformApp } from '@typings';
import { useHelmetTitle } from '@hooks';
import { capitalize } from '@utils';

import { ChaseSpinner, Helmet, Theme } from '@components';
import { Layout } from '@components/Layouts';
import { PlatformAppsInstanceTabs } from '@components/Ui';
import { PlatformAppManagementHandleCenter } from '@components/Ui/PlatformApps';

type Props = {
  instance: PlatformApp.Instance;
  input: OutletContext.PlatformAppsInstance['input'];
  output: OutletContext.PlatformAppsInstance['output'];
  getInstance: () => Promise<PlatformApp.Instance>;
  getInstanceParams: () => void;
};

export const PlatformAppManagementLayout: React.FC<Props> = ({
  instance,
  input,
  output,
  instance: { displayName, name, id: instanceId },
  getInstance,
  getInstanceParams,
}) => {
  const { makeTitle } = useHelmetTitle();

  const [tabTitle, setTabTitle] = useState<Job.Tab>();

  const title = makeTitle(displayName ?? name, tabTitle);

  const handleTabTitleChange = useCallback((tab: Job.Tab) => {
    setTabTitle(capitalize(tab) as Job.Tab);
  }, []);

  const refreshInstance = async () => {
    try {
      await getInstance();
      getInstanceParams();
    } catch (error) {
      /**
       * Continue regardless error
       */
    }
  };

  const renderContent = () => {
    if (!instance) {
      return (
        <Layout.Content className="relative flex justify-center">
          <ChaseSpinner color="black" className="absolute mt-40 h-14 w-14" />
        </Layout.Content>
      );
    }

    return (
      <Layout.Content>
        <PlatformAppManagementHandleCenter
          instanceId={instanceId}
          getInstance={refreshInstance}
        />
        <Theme.Container className="relative p-0">
          <PlatformAppsInstanceTabs instanceId={instanceId} />
          <Outlet
            context={{
              instance,
              input,
              output,
              getInstance,
              setTabTitle: handleTabTitleChange,
            }}
          />
        </Theme.Container>
      </Layout.Content>
    );
  };

  return (
    <Layout backNavigation title={title}>
      <Helmet title={title} />
      {renderContent()}
    </Layout>
  );
};
